// AdminFrotaPage — gestão da frota e vínculo veículo↔técnico
import { useEffect, useState, useMemo } from 'react';
import { admin } from '../../lib/api.js';

function formatTs(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

function StatusGps({ veiculo }) {
  const p = veiculo.posicao;
  if (!p || p.lat == null) {
    return <span style={{ color: '#9ca3af', fontSize: 11 }}>sem GPS</span>;
  }
  const idade = veiculo.posicao_idade_min;
  const cor = idade > 60 ? '#dc2626' : idade > 15 ? '#d97706' : '#059669';
  return (
    <div style={{ fontSize: 11, lineHeight: 1.3 }}>
      <div style={{ color: cor, fontWeight: 600 }}>
        {idade != null ? (idade === 0 ? 'agora' : `há ${idade}min`) : '—'}
      </div>
      <div style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 10 }}>
        {p.lat.toFixed(4)}, {p.lon.toFixed(4)}
      </div>
      <div style={{ color: '#6b7280' }}>
        {p.ignicao === true ? '🔑 ligada' : p.ignicao === false ? '○ desligada' : ''}
        {p.velocidade_kmh != null && ` · ${p.velocidade_kmh.toFixed(0)} km/h`}
      </div>
    </div>
  );
}

export default function AdminFrotaPage() {
  const [veiculos, setVeiculos] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [salvandoId, setSalvandoId] = useState(null);

  async function recarregar() {
    try {
      const [frota, tecs] = await Promise.all([
        admin.listarFrota(),
        admin.tecnicosDisponiveis(),
      ]);
      setVeiculos(frota.data || []);
      setTecnicos(tecs.data || []);
      setErro(null);
    } catch (e) { setErro(e.uiMessage || 'Erro ao carregar'); }
    setLoading(false);
  }

  useEffect(() => {
    recarregar();
    const h = setInterval(recarregar, 60_000);  // auto-refresh 1min pra GPS
    return () => clearInterval(h);
  }, []);

  async function alterarTecnico(veiculo, novoTecnicoId) {
    const tecnicoId = novoTecnicoId === '' ? null : Number(novoTecnicoId);
    if (veiculo.tecnico_id === tecnicoId) return;
    setSalvandoId(veiculo.id);
    try {
      await admin.atualizarVeiculo(veiculo.id, { tecnico_id: tecnicoId });
      await recarregar();
    } catch (e) {
      alert(e.uiMessage || 'Erro');
    }
    setSalvandoId(null);
  }

  async function toggleAtivo(veiculo) {
    const action = veiculo.ativo ? 'desativar' : 'reativar';
    if (!confirm(`${action[0].toUpperCase() + action.slice(1)} veículo ${veiculo.placa}?`)) return;
    setSalvandoId(veiculo.id);
    try {
      await admin.atualizarVeiculo(veiculo.id, { ativo: !veiculo.ativo });
      await recarregar();
    } catch (e) { alert(e.uiMessage); }
    setSalvandoId(null);
  }

  // Estatísticas
  const stats = useMemo(() => {
    const total = veiculos.length;
    const ativos = veiculos.filter(v => v.ativo).length;
    const vinculados = veiculos.filter(v => v.tecnico_id && v.ativo).length;
    const comGps = veiculos.filter(v => v.posicao && v.posicao.lat != null).length;
    return { total, ativos, vinculados, comGps };
  }, [veiculos]);

  return (
    <div className="stack">
      <h1>Frota</h1>
      <p style={{ color: '#6b7280', marginTop: -8, marginBottom: 16 }}>
        Veículos cadastrados na Upgrade Mobilidade e vínculo com técnicos do Field Service.
        Sincronização automática a cada hora — vínculo manual via tabela abaixo.
      </p>

      {erro && <div className="card error">{erro}</div>}

      <div className="kpi-cards" style={{ marginBottom: 16 }}>
        <div className="kpi-card">
          <div className="kpi-label">Total</div>
          <div className="kpi-value">{stats.total}</div>
          <div className="kpi-sublabel">{stats.ativos} ativos</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Vinculados a técnico</div>
          <div className="kpi-value">{stats.vinculados}</div>
          <div className="kpi-sublabel">de {stats.ativos} ativos</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Reportando GPS</div>
          <div className="kpi-value">{stats.comGps}</div>
          <div className="kpi-sublabel">com posição recente</div>
        </div>
      </div>

      {loading && <div className="muted">Carregando frota...</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Placa</th>
              <th>Identificação</th>
              <th>IMEI / Provedor</th>
              <th>Técnico vinculado</th>
              <th>Status GPS</th>
              <th>Cadastro</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {veiculos.map(v => {
              const isSaving = salvandoId === v.id;
              return (
                <tr key={v.id} style={{ opacity: v.ativo ? 1 : 0.5 }}>
                  <td>
                    <strong style={{ fontFamily: 'monospace' }}>{v.placa || '—'}</strong>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <div>{v.nome || '—'}</div>
                    <div style={{ color: '#9ca3af', fontSize: 11 }}>{v.modelo || ''}</div>
                  </td>
                  <td style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
                    {v.imei || '—'}
                    <div style={{ fontSize: 10 }}>{v.provedor || '—'}</div>
                  </td>
                  <td>
                    <select
                      value={v.tecnico_id || ''}
                      onChange={e => alterarTecnico(v, e.target.value)}
                      disabled={isSaving || !v.ativo}
                      style={{
                        padding: '4px 8px',
                        border: v.tecnico_id ? '1px solid #d1d5db' : '1px solid #fbbf24',
                        borderRadius: 4,
                        background: v.tecnico_id ? 'white' : '#fef3c7',
                        fontSize: 12,
                        minWidth: 200,
                      }}
                    >
                      <option value="">— sem técnico —</option>
                      {tecnicos.map(t => (
                        <option key={t.id} value={t.id}
                          disabled={t.veiculo_id && t.veiculo_id !== v.id}>
                          {t.apelido || t.nome}
                          {t.veiculo_id && t.veiculo_id !== v.id ? ` (já em ${t.veiculo_placa})` : ''}
                        </option>
                      ))}
                    </select>
                    {isSaving && <span style={{ marginLeft: 6, fontSize: 11, color: '#6b7280' }}>salvando…</span>}
                  </td>
                  <td>
                    <StatusGps veiculo={v} />
                  </td>
                  <td style={{ fontSize: 11, color: '#6b7280' }}>
                    {formatTs(v.dt_criacao).split(',')[0]}
                  </td>
                  <td>
                    <span className={`status-pill ${v.ativo ? 'ativo' : 'inativo'}`}>
                      {v.ativo ? 'ativo' : 'inativo'}
                    </span>
                  </td>
                  <td>
                    <button className="btn small secondary"
                            onClick={() => toggleAtivo(v)}
                            disabled={isSaving}>
                      {v.ativo ? '⏸ Desativar' : '▶ Reativar'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {veiculos.length === 0 && !loading && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>
                  Nenhum veículo cadastrado. A sincronização automática vai puxar da Upgrade Mobilidade.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="alert-block" style={{
        background: '#eff6ff', borderLeftColor: '#3b82f6', color: '#1e40af',
      }}>
        💡 <strong>Como funciona:</strong> Quando um veículo novo é cadastrado na <em>Upgrade Mobilidade</em>,
        ele aparece aqui automaticamente em até 1 hora (sincronização periódica). Para que o veículo
        comece a aparecer no Dashboard com o avatar do técnico, basta selecionar o técnico no dropdown
        acima — não é necessário fazer mais nada.
      </div>
    </div>
  );
}
